/**
 * The referral code a colleague gave, on the public checkout.
 *
 * Reads `?ref=CODE` from the address and keeps it under `referral_code_v1` in
 * localStorage for seven days, so someone who opens the link on Monday and
 * enrols on Wednesday is still linked to the colleague. The server
 * (`POST /api/referral/lookup`) is the only one that says whether a code can
 * be used; nothing here decides.
 *
 * The consumer mounts the field only while `settings.referral_enabled` is on.
 */
import { api } from "./api";

const STORAGE_KEY = "referral_code_v1";
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Read `?ref=CODE` (or `?referral=CODE`) from the URL, normalise to uppercase
 * and persist in localStorage with a timestamp. Idempotent: safe to call on
 * every Checkout/Landing mount.
 */
export function captureReferralFromUrl() {
  try {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("ref") || params.get("referral");
    if (!raw) return null;
    const code = raw.trim().toUpperCase();
    if (!code || code.length < 4 || code.length > 16) return null;
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ code, ts: Date.now() })
    );
    return code;
  } catch {
    return null;
  }
}

/** Return the stored referral code if still within TTL. */
export function readStoredReferralCode() {
  try {
    if (typeof window === "undefined") return "";
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    if (!parsed?.code || !parsed?.ts) return "";
    if (Date.now() - parsed.ts > TTL_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return "";
    }
    return parsed.code;
  } catch {
    return "";
  }
}

/** Drop the stored code (post successful submit or on user request). */
export function clearStoredReferralCode() {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // best-effort
  }
}

/**
 * Ask the server about a code. `email` is what is typed in the form so far —
 * it lets the answer say "this is your own code" — and travels in the body,
 * never in the address. Network trouble comes back as `{valid: false, reason:
 * "network"}` and too many tries as `"rate_limited"`, so the form stays calm.
 */
export async function lookupReferral(code, email = "") {
  const normalized = (code || "").trim().toUpperCase();
  if (!normalized) return { valid: false, reason: "missing_code" };
  try {
    const r = await api.post("/referral/lookup", { code: normalized, email: (email || "").trim() || null });
    return r.data || { valid: false, reason: "unknown" };
  } catch (err) {
    return { valid: false, reason: err?.response?.status === 429 ? "rate_limited" : "network" };
  }
}

/**
 * What the checkout says under the field, for an answer of the server:
 * `{ tone: "ok" | "warn", text, name?, benefit? }`, or null when there is
 * nothing to say. The wording was validated by FPED on 13.10.2026.
 */
export function referralMessage(result) {
  if (!result) return null;
  if (result.valid) {
    return {
      tone: "ok",
      text: "Cod valid. Te-a recomandat",
      name: result.owner_display || "un coleg",
      benefit: (result.referred_benefit || "").trim(),
    };
  }
  const text = {
    not_found: "Nu găsim acest cod. Verifică-l sau continuă fără el.",
    expired: "Acest cod nu mai e valabil. Poți continua fără el.",
    cancelled: "Acest cod nu mai e valabil. Poți continua fără el.",
    own_code: "Acesta e codul tău. Nu îl poți folosi la propria înscriere.",
    rate_limited: "Prea multe încercări. Mai încearcă peste câteva minute.",
    network: "Nu am putut verifica codul acum. Mai încearcă o dată.",
    unknown: "Nu am putut verifica codul acum. Mai încearcă o dată.",
  }[result.reason];
  return text ? { tone: "warn", text } : null;
}

/** Compose the public shareable invite link for a given code. Points to
 *  `/checkout?ref=` so the invitee lands directly on the enrollment form
 *  with the code pre-filled (no extra hop through the homepage). */
export function buildShareLink(code) {
  if (typeof window === "undefined") return `https://ai.fped.org/checkout?ref=${code}`;
  const base = `${window.location.protocol}//${window.location.host}/checkout`;
  return `${base}?ref=${encodeURIComponent(code)}`;
}
